import type { FilterContentProps } from './WalletDetailsFilter.types';
import type { ReactElement } from 'react';

import { CheckboxCards, Flex, ScrollArea, Text } from '@rango-dev/whale';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks';
import { useWalletDetails } from '../../../hooks/useWalletDetails';
import { setExcludedBlockchains } from '../../../state/WalletDetailsSlice';
import {
  getBlockchainDisplayName,
  getBlockchainFullName,
  getBlockchainLogo,
} from '../../../utils/Constants';
import { containsText } from '../../../utils/generalFunctions/common';

import { WalletDetailFilterHeader } from './WalletDetailFilterHeader';
import styles from './WalletDetailsFilter.module.css';

const SYMBOL_SIZE = 32;

export function BlockchainFilter(props: FilterContentProps): ReactElement {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchedFor, setSearchedFor] = useState<string>('');
  const { toggleExcludedAllBlockchain, validBlockchains } = useWalletDetails();
  const allBlockchains = useAppSelector((state) => state.meta.blockchains);
  const { excludedBlockchains } = useAppSelector((state) => state.walletDetails);

  const blockchainList = useMemo(() => {
    return validBlockchains.map((blockchainItem) => {
      const logo = getBlockchainLogo(blockchainItem, allBlockchains);
      const displayName = getBlockchainDisplayName(blockchainItem, allBlockchains);
      const fullName = getBlockchainFullName(blockchainItem, allBlockchains);
      return { logo, displayName, name: blockchainItem, fullName };
    });
  }, [validBlockchains, allBlockchains]);

  let filteredList = blockchainList;
  if (searchedFor) {
    filteredList = blockchainList.filter(
      (blockchainItem) =>
        containsText(blockchainItem.name, searchedFor) ||
        containsText(blockchainItem.displayName, searchedFor) ||
        (blockchainItem.fullName && containsText(blockchainItem.fullName, searchedFor)),
    );
  }

  const selectedBlockchains = validBlockchains.filter(
    (blockchainItem) => !excludedBlockchains.includes(blockchainItem),
  );

  const handleValueChange = (nextSelected: string[]) => {
    dispatch(
      setExcludedBlockchains(
        validBlockchains.filter((blockchainItem) => !nextSelected.includes(blockchainItem)),
      ),
    );
  };

  return (
    <WalletDetailFilterHeader
      title={t('Chains')}
      showHeader={props.showHeader}
      handleToggleAll={toggleExcludedAllBlockchain}
      excludedItems={excludedBlockchains}
      searchHandler={setSearchedFor}
      searchedFor={searchedFor}
      searchInputId="wallet-details-blockchain-filter-search-input"
      toggleButtonId="wallet-details-blockchain-filter-toggle-all-btn"
    >
      <ScrollArea type="auto" scrollbars="vertical" className={styles.list}>
        <CheckboxCards.Root
          size="1"
          columns="1"
          gap="2"
          value={selectedBlockchains}
          onValueChange={handleValueChange}
        >
          {filteredList.map((blockchainItem) => (
            <CheckboxCards.Item key={blockchainItem.name} value={blockchainItem.name}>
              <Flex align="center" gap="2" flexGrow="1" minWidth="0">
                <img
                  className={styles.symbol}
                  src={blockchainItem.logo}
                  alt={blockchainItem.displayName}
                  width={SYMBOL_SIZE}
                  height={SYMBOL_SIZE}
                />
                <Text size="2" weight="medium" truncate>
                  {blockchainItem.displayName}
                </Text>
              </Flex>
            </CheckboxCards.Item>
          ))}
        </CheckboxCards.Root>
      </ScrollArea>
    </WalletDetailFilterHeader>
  );
}
